<template>
  <div class="property-manager-view container">
    <transition name="fade">
      <div
        :class="showFilters && isDesktop ? 'property-manager-view--overlay' : ''"
        @click="setShowFilters(false)"
      />
    </transition>

    <BLoading :active.sync="isLoading" />

    <div
      v-if="!isLoading && (isDesktop || (!isDesktop && !showFilters))"
      :class="{ 'disable-click': showFilters && isDesktop }"
    >
      <h1>Properties</h1>

      <LsTopLevelSearchInput
        v-model="searchInput"
        class="property-manager-view--search"
        :green-line="true"
        track-id="property_manager_view-search"
        @input="onSearchInput"
      />

      <div class="property-manager-view--filter" @click="setShowFilters(!showFilters)">
        <span :class="filterLength && !isDesktop ? 'primary' : ''">
          Filter {{ isDesktop ? 'by:' : '' }}
        </span>
        <div :class="filterLength ? 'primary' : ''">
          <span v-if="isDesktop">Status</span> <span v-if="filterLength">({{ filterLength }})</span>
          <LsIcon
            type="chevron"
            :color="filterLength ? 'primary' : 'gray-primary'"
            :class="['property-manager-view--filter-icon', { rotate: isDesktop && showFilters }]"
          />
        </div>
      </div>

      <NoSearchResultsFound
        v-if="!allProperties.length"
        heading="No Properties Were Found"
        body="Try a different keyword or filter to find what you're looking for"
      />

      <div v-else>
        <PropertyTable
          v-if="isDesktop"
          :properties="onePageOfProperties"
          :nav-to-my-services="navToMyServices"
          :sort="setSort"
          :sorting="propertyManager.sorting"
        />
        <PropertyTableMobile
          v-else
          :properties="onePageOfProperties"
          :nav-to-my-services="navToMyServices"
          :sort="setSort"
          :sorting="propertyManager.sorting"
        />

        <LsPagination :pagination="pagination" :change-page="changePage" />
      </div>
    </div>
    <!-- this needs to be here to accomodate full screen on mobile -->
    <transition name="fade">
      <PropertyFilterPopover
        v-if="showFilters"
        :is-desktop="isDesktop"
        :change-page="changePage"
        :set-show-filters="setShowFilters"
      />
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import {
  PROPERTY_MANAGER__PROPERTIES,
  PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID,
  PROPERTY_MANAGER__GET_PROPERTIES,
  AUTH__IS_PROPERTY_MANAGER,
  PROPERTY_MANAGER__UPDATE_SEARCH_QUERY,
  PROPERTY_MANAGER__UPDATE_SORT,
} from '@/store/types';
import { LsTopLevelSearchInput } from '@/components';
import { LsPagination, LsIcon } from '@/components/common';
import _ from 'lodash';
import { lsToastService, lsTrackingService } from 'ls-vue-common';
import PropertyTable from './components/PropertyTable/PropertyTable.vue';
import PropertyTableMobile from './components/PropertyTableMobile/PropertyTableMobile.vue';
import NoSearchResultsFound from '@/views/Authorized/SupportView/components/NoSearchResultsFound/NoSearchResultsFound.vue';
import PropertyFilterPopover from './components/PropertyFilterPopover/PropertyFilterPopover.vue';

export default {
  name: 'PropertyManagerView',
  components: {
    LsPagination,
    PropertyTable,
    PropertyTableMobile,
    LsTopLevelSearchInput,
    NoSearchResultsFound,
    LsIcon,
    PropertyFilterPopover,
  },
  data() {
    return {
      showFilters: false,
      searchInput: '',
    };
  },
  computed: {
    ...mapState(['propertyManager', 'screenSize', 'auth']),
    ...mapGetters({
      allProperties: [PROPERTY_MANAGER__PROPERTIES],
      isPropertyManager: [AUTH__IS_PROPERTY_MANAGER],
    }),
    isLoading() {
      return this.propertyManager.loadingStatus.isLoading;
    },
    isDesktop() {
      return this.screenSize.desktop;
    },
    filterLength() {
      return _.size(this.propertyManager.filter);
    },
    pagination() {
      return {
        totalItems: this.isLoading ? 0 : _.size(this.allProperties),
        currentPage: _.toNumber(_.get(this.$route.params, 'page')) || 1,
        itemsPerPage: 10,
      };
    },
    onePageOfProperties() {
      const { currentPage, itemsPerPage } = this.pagination;
      const startIdx = currentPage === 1 ? 0 : currentPage * itemsPerPage - 10;

      return _.slice(this.allProperties, startIdx, startIdx + itemsPerPage);
    },
    lastPage() {
      // first check if we have allProperties set, if not set, use the total property count on the customer
      const numberOfProperties =
        this.allProperties.length || this.auth.user.customer.property_count;

      return Math.ceil(numberOfProperties / this.pagination.itemsPerPage);
    },
  },
  beforeRouteUpdate(to, from, next) {
    // prevent ending up on a page that should not exist when searching/filtering. ie, you are on page 3, then search
    // and we end up with only 2 pages once the list shortens. Then hit the back button to go to page 3.
    // This way, we go to page 2 as it's the last page now.
    if ((this.searchInput || this.filterLength) && _.toNumber(to.params.page) > this.lastPage) {
      this.$router.push({
        params: {
          page: this.lastPage,
        },
      });
    } else {
      next();
    }
  },
  async created() {
    if (!this.isPropertyManager) {
      return this.$router.replace({ name: 'services' });
    }

    await this[PROPERTY_MANAGER__GET_PROPERTIES]();

    this.searchInput = this.propertyManager.searchString;

    if (_.toNumber(_.get(this.$route.params, 'page')) > this.lastPage) {
      this.$router.push({
        params: {
          page: 1,
        },
      });
    }

    if (_.get(this.$route.params, 'toRoute')) {
      lsToastService.open({
        message: 'Please choose a property before proceeding',
        type: 'is-tertiary',
        duration: 4000,
      });
    }
  },

  methods: {
    ...mapActions([
      PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID,
      PROPERTY_MANAGER__GET_PROPERTIES,
      PROPERTY_MANAGER__UPDATE_SEARCH_QUERY,
      PROPERTY_MANAGER__UPDATE_SORT,
    ]),

    setShowFilters(value) {
      this.showFilters = value;
    },

    setSort({ type }) {
      this[PROPERTY_MANAGER__UPDATE_SORT]({ type });

      lsTrackingService.track({
        event: 'click',
        target: 'property_manager_view-sort',
        data: { value: type },
      });
    },

    onSearchInput(value) {
      if (this.pagination.currentPage !== 1) {
        this.changePage(1);
      }

      this[PROPERTY_MANAGER__UPDATE_SEARCH_QUERY]({ query: value });
    },

    changePage(page) {
      this.$router.push({
        params: {
          page,
        },
      });
    },

    navToMyServices({ propertyID }, e) {
      if (!e || e.keyCode === 13 || e.keyCode === 32) {
        this[PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID]({ propertyID });

        this.$router.push({
          name: _.get(this.$route.params, 'toRoute', 'services'),
          params: _.get(this.$route.params, 'passThroughParams', {}),
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.property-manager-view {
  /* TODO: because we are not showing the links in navBar, the container is
    is shifting up so need to push it back down.  Once the navBar is done right we won't
    need the margin anymore
    */
  margin-top: 110px;

  .property-manager-view--overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    opacity: 0.7;
    background-color: $white;
    z-index: $z-index-property-manager-view-overlay;
  }

  &.disable-click {
    pointer-events: none;
    user-select: none;
  }

  h1 {
    padding: 0 0 40px 0;
  }

  .property-manager-view--search {
    margin-bottom: 15px;
  }

  .property-manager-view--filter {
    padding: 20px 0 20px 10px;
    font-weight: bold;
    display: flex;
    align-items: center;

    .primary {
      color: $primary;
    }

    > span:first-child {
      margin-right: 10px;
      cursor: pointer;

      @media (max-width: $mobile-max-width) {
        margin-right: 3px;
      }
    }

    > div {
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
    }

    .property-manager-view--filter-icon {
      margin-left: 2px;
      transition: transform 0.1s linear;
      transform: translateY(1px);

      &.rotate {
        transform: rotate(90deg);
      }
    }
  }
}
.themed-app {
  .property-manager-view {
    .property-manager-view--overlay {
      background-color: $theme-color-white;
    }
    .property-manager-view--filter {
      .primary {
        color: $theme-color-primary;
      }
    }
  }
}
</style>
